import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useMutation } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { limitFragment } from '@/fragments/Limits';
import { LimitTypeEnum } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  RemoveGlobalLossLimitMutation,
  RemoveGlobalLossLimitMutationVariables,
} from './__generated__/PlayerRemoveGlobalLossLimitForm';

const query = graphql`
  query SanityPlayerRemoveGlobalLossLimitForm {
    sanityPlayerRemoveGlobalLossLimitsForm {
      title {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const removeGlobalLossLimitMutation = gql`
  mutation RemoveGlobalLossLimit(
    $playerId: ID!
    $limitPeriodType: LimitTypeEnum!
  ) {
    removeGlobalLossLimitByPlayerId(
      playerId: $playerId
      limitPeriodType: $limitPeriodType
    ) {
      id
      lossLimit {
        dayLimit {
          ...PeriodLimit
        }
        weekLimit {
          ...PeriodLimit
        }
        monthLimit {
          ...PeriodLimit
        }
      }
    }
  }
  ${limitFragment}
`;

const PlayerRemoveGlobalLossLimitForm: FC<{
  playerId: string;
  limitPeriodType: LimitTypeEnum;
}> = ({ playerId, limitPeriodType }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerRemoveGlobalLossLimitFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerRemoveGlobalLossLimitsForm;

  const [removePlayerGlobalLossLimitState, removePlayerGlobalLossLimit] =
    useMutation<
      RemoveGlobalLossLimitMutation,
      RemoveGlobalLossLimitMutationVariables
    >(removeGlobalLossLimitMutation);

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (_values: {}) => {
    if (playerId) {
      removePlayerGlobalLossLimit({
        playerId,
        limitPeriodType,
      }).then((res) => {
        if (res.error?.message && isMounted) {
          setErrorMessage(res.error?.message);
          return;
        }
        if (close) {
          close();
        }
      });
    }
  };

  if (!form) {
    return null;
  }
  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={{}}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={removePlayerGlobalLossLimitState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerRemoveGlobalLossLimitForm;
